import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
    Calendar, Link2, Inbox, Archive, Video, Mic, FileText, ListChecks,
    CheckCircle2, Trash2, ChevronLeft, ExternalLink, Play, Users, Clock,
    Send, AlertCircle, RefreshCw, Loader, Plus, X, Sparkles, Volume2,
} from "lucide-react";
import "./PlaceholderPage.css";
import "./RegistrazioniCall.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// --- Helper UI ---------------------------------------------------------------

const PLATFORM_LABEL = { meet: "Google Meet", teams: "Microsoft Teams", zoom: "Zoom", webex: "Webex", other: "Videocall" };

const PlatformBadge = ({ platform }) => (
    <span className={`rc-platform rc-platform-${platform}`}>
        <Video size={13} />
        {PLATFORM_LABEL[platform] || platform}
    </span>
);

const fmtData = (s) => {
    if (!s) return "";
    // Supporta sia "2026-06-12 09:30" che ISO "2026-06-12T09:30:00Z"
    const d = new Date(s);
    if (isNaN(d)) return s;
    return d.toLocaleDateString("it-IT") + " · " + d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
};

const fmtDurata = (min) => {
    if (!min) return "";
    return min >= 60 ? `${Math.floor(min / 60)}h ${min % 60}m` : `${min}m`;
};

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// Timeout sulle GET di lista/dettaglio: una richiesta appesa non deve tenere la
// pagina "in caricamento" per minuti (era il sintomo della lista da 80 MB).
const GET_TIMEOUT_MS = 20000;
const POLL_MS = 30000;
const POLL_MS_ATTIVO = 15000;
const POLL_BACKOFF_MAX_MS = 5 * 60 * 1000;
const ARCHIVIO_PAGE = 50;

const messaggioErrore = (err, fallback) => {
    if (err?.code === "ECONNABORTED") return "Il server non ha risposto in tempo.";
    if (err?.response?.status === 401) return "Sessione scaduta: effettua di nuovo l'accesso.";
    return err?.response?.data?.error || fallback;
};

// --- Componente --------------------------------------------------------------

const RegistrazioniCall = () => {
    const [activeTab, setActiveTab] = useState("calendario");

    // Stato calendario (reale, da API)
    const [calendarStatus, setCalendarStatus] = useState(null); // null = caricamento
    const [calendarLoading, setCalendarLoading] = useState(true);
    const [calendarError, setCalendarError] = useState(null);
    const [meetings, setMeetings] = useState([]);
    const [meetingsLoading, setMeetingsLoading] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [linkInput, setLinkInput] = useState("");
    const [linkLoading, setLinkLoading] = useState(false);
    const [linkSuccess, setLinkSuccess] = useState(null);
    const [oauthLoading, setOauthLoading] = useState(false);
    const [hasActiveBots, setHasActiveBots] = useState(false);

    // Tab Da revisionare / Archivio (reali da API). Le liste arrivano SENZA
    // trascrizione: la scheda completa si carica all'apertura (GET /:id → detail).
    const [drafts, setDrafts] = useState([]);
    // Filtro "Da revisionare": tutte | mie (sono il revisore) | sola_lettura (sono tra i partecipanti)
    const [draftsFilter, setDraftsFilter] = useState("tutte");
    const [draftsLoading, setDraftsLoading] = useState(true);
    const [draftsError, setDraftsError] = useState(null);
    const [archive, setArchive] = useState([]);
    const [archiveLoaded, setArchiveLoaded] = useState(false);
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [archiveError, setArchiveError] = useState(null);
    const [archiveHasMore, setArchiveHasMore] = useState(false);
    const [archiveNextBeforeId, setArchiveNextBeforeId] = useState(null);
    const [selectedRecordingId, setSelectedRecordingId] = useState(null);
    const [detail, setDetail] = useState(null); // scheda completa (con transcript_testo) della call aperta
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    // Richieste in volo e backoff del polling (una lista alla volta, niente sovrapposizioni)
    const draftsAbortRef = useRef(null);
    const pollFailuresRef = useRef(0);
    const [editRiassunto, setEditRiassunto] = useState("");
    const [editTitolo, setEditTitolo] = useState("");
    const [editSchema, setEditSchema] = useState([]);
    const [editActionItems, setEditActionItems] = useState([]);
    // Autosave sintesi: debounce degli edit + skip del primo run subito dopo apriRevisione
    // (che imposta gli stati con i valori dal DB e non deve ri-salvarli).
    const sintesiSaveTimer = useRef(null);
    const skipSintesiSaveRef = useRef(true);

    const allRecordings = useMemo(() => [...drafts, ...archive], [drafts, archive]);
    const draftsMie = useMemo(() => drafts.filter((d) => d.is_owner !== false), [drafts]);
    const draftsSolaLettura = useMemo(() => drafts.filter((d) => d.is_owner === false), [drafts]);
    // Il filtro ha senso solo se c'è almeno una scheda in sola lettura; altrimenti
    // la barra non compare e la lista resta completa (anche con un filtro selezionato
    // in precedenza, così non si resta su una vista vuota senza pulsanti).
    const filtroDisponibile = draftsSolaLettura.length > 0;
    const draftsVisibili = !filtroDisponibile ? drafts
        : draftsFilter === "mie" ? draftsMie
        : draftsFilter === "sola_lettura" ? draftsSolaLettura
        : drafts;
    // Riga di lista + campi del dettaglio (trascrizione). Il dettaglio da solo basta
    // per le call aperte da deep-link che non stanno nella pagina di lista caricata.
    const selectedDraft = useMemo(() => {
        if (!selectedRecordingId) return null;
        const base = allRecordings.find((d) => d.id === selectedRecordingId) || null;
        const det = detail && detail.id === selectedRecordingId ? detail : null;
        if (!base && !det) return null;
        return { ...(base || {}), ...(det || {}) };
    }, [allRecordings, detail, selectedRecordingId]);
    const isArchived = selectedDraft?.stato_pipeline === "pubblicata";
    // Owner = piena revisione/pubblicazione. Viewer (co-partecipante) = sola lettura.
    // is_owner assente su dati vecchi → default true (retrocompatibilità).
    const isOwner = selectedDraft ? selectedDraft.is_owner !== false : true;
    const isReadOnly = isArchived || !isOwner;

    const [mediaVideoUrl, setMediaVideoUrl] = useState(null);
    const [mediaAudioUrl, setMediaAudioUrl] = useState(null);
    const [mediaMode, setMediaMode] = useState("video"); // "video" | "audio"
    const [mediaLoading, setMediaLoading] = useState(false);
    const mediaRef = useRef(null); // <video>/<audio> attivo, per il seek dalle citazioni

    // Chatbot "Chiedi alla call"
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState("");
    const [chatStreaming, setChatStreaming] = useState(false);
    const chatEndRef = useRef(null);

    // Stato pubblicazione su Interacta
    const [interactaUsersForPublish, setInteractaUsersForPublish] = useState([]);
    const [loadingInteractaUsers, setLoadingInteractaUsers] = useState(false);
    // Rubrica Interacta completa — suggerimenti per l'owner degli action item (testo libero + selezione)
    const [interactaUsersAll, setInteractaUsersAll] = useState([]);
    const [ownerComboOpen, setOwnerComboOpen] = useState(null); // indice action item col dropdown owner aperto
    const [publishing, setPublishing] = useState(false);
    const [publishError, setPublishError] = useState(null);

    // Condivisione revisione (aggiungi/rimuovi viewer read-only)
    const [shareSearch, setShareSearch] = useState("");
    const [addingViewer, setAddingViewer] = useState(false);
    const [shareError, setShareError] = useState(null);

    // Deep-link (?call=<id>) — apertura diretta di una scheda call, es. dal link nel post Interacta
    const [deepLinkError, setDeepLinkError] = useState(null);

    // ── Carica registrazioni da revisionare ──────────────────────────────────

    // Lista "Da revisionare" (leggera: niente trascrizione). Una richiesta alla
    // volta: se ne arriva una nuova mentre la precedente è in volo, la precedente
    // viene annullata. Errori mostrati in pagina, non solo in console.
    const loadDrafts = useCallback(async ({ silent = false } = {}) => {
        if (draftsAbortRef.current) draftsAbortRef.current.abort();
        const controller = new AbortController();
        draftsAbortRef.current = controller;
        if (!silent) setDraftsLoading(true);
        try {
            const res = await axios.get(`${API_URL}/registrazioni`, {
                params: { stato: "revisione", limit: 200 },
                headers: authHeader(),
                timeout: GET_TIMEOUT_MS,
                signal: controller.signal,
            });
            setDrafts(res.data.data || []);
            setDraftsError(null);
            pollFailuresRef.current = 0;
            return true;
        } catch (err) {
            if (axios.isCancel(err)) return false;
            console.error("loadDrafts:", err);
            setDraftsError(messaggioErrore(err, "Impossibile caricare le registrazioni da revisionare."));
            pollFailuresRef.current += 1;
            return false;
        } finally {
            if (draftsAbortRef.current === controller) {
                draftsAbortRef.current = null;
                setDraftsLoading(false);
            }
        }
    }, []);

    // Conteggi per polling/badge: aggiorna la lista solo se è cambiato qualcosa.
    const loadCount = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/registrazioni/count`, { headers: authHeader(), timeout: GET_TIMEOUT_MS });
            setHasActiveBots(!!res.data.inPipeline);
            pollFailuresRef.current = 0;
            return res.data;
        } catch (err) {
            pollFailuresRef.current += 1;
            return null;
        }
    }, []);

    // Archivio a pagine (ARCHIVIO_PAGE per volta, paginazione keyset per id).
    const loadArchive = useCallback(async ({ reset = true, beforeId = null } = {}) => {
        setArchiveLoading(true);
        setArchiveError(null);
        try {
            const res = await axios.get(`${API_URL}/registrazioni`, {
                params: { stato: "pubblicata", limit: ARCHIVIO_PAGE, ...(beforeId ? { before_id: beforeId } : {}) },
                headers: authHeader(),
                timeout: GET_TIMEOUT_MS,
            });
            const rows = res.data.data || [];
            setArchive((prev) => (reset ? rows : [...prev, ...rows]));
            setArchiveHasMore(!!res.data.hasMore);
            setArchiveNextBeforeId(res.data.nextBeforeId || null);
            setArchiveLoaded(true);
        } catch (err) {
            console.error("loadArchive:", err);
            setArchiveError(messaggioErrore(err, "Impossibile caricare l'archivio."));
        } finally {
            setArchiveLoading(false);
        }
    }, []);

    // Dopo un'azione che sposta una call tra le liste (archivia, riporta, elimina,
    // pubblica): ricarica Da revisionare e, se già aperto, l'archivio dalla prima pagina.
    const refreshLists = useCallback(async () => {
        await Promise.all([loadDrafts({ silent: true }), loadCount(), archiveLoaded ? loadArchive({ reset: true }) : null]);
    }, [loadDrafts, loadCount, loadArchive, archiveLoaded]);

    // Scheda completa (trascrizione) per owner o viewer.
    const loadDetail = useCallback(async (id) => {
        setDetailLoading(true);
        setDetailError(null);
        try {
            const res = await axios.get(`${API_URL}/registrazioni/${id}`, { headers: authHeader(), timeout: GET_TIMEOUT_MS });
            setDetail(res.data);
            return res.data;
        } catch (err) {
            console.error("loadDetail:", err);
            setDetailError(err?.response?.status === 404
                ? "La call richiesta non è stata trovata, oppure non hai accesso a questa registrazione."
                : messaggioErrore(err, "Impossibile caricare la scheda della call."));
            return null;
        } finally {
            setDetailLoading(false);
        }
    }, []);

    // ── Carica stato calendario ───────────────────────────────────────────────

    const loadCalendarStatus = useCallback(async () => {
        setCalendarLoading(true);
        setCalendarError(null);
        try {
            const res = await axios.get(`${API_URL}/registrazioni/calendar/status`, { headers: authHeader() });
            setCalendarStatus(res.data);
            if (res.data.connected) loadCalendarEvents();
        } catch (err) {
            setCalendarError("Impossibile caricare lo stato del calendario.");
        } finally {
            setCalendarLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadCalendarEvents = async () => {
        setMeetingsLoading(true);
        try {
            const res = await axios.get(`${API_URL}/registrazioni/calendar/events`, { headers: authHeader() });
            setMeetings(res.data.events || []);
        } catch (err) {
            console.error("loadCalendarEvents:", err);
        } finally {
            setMeetingsLoading(false);
        }
    };

    // Controlla URL params al mount (redirect da OAuth)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const calendarParam = params.get("calendar");
        const errorParam = params.get("error");
        const callParam = params.get("call");

        if (callParam) {
            window.history.replaceState({}, "", window.location.pathname);
            apriDeepLink(callParam);
        }
        if (calendarParam === "connected") {
            window.history.replaceState({}, "", window.location.pathname);
            setActiveTab("calendario");
        }
        if (errorParam) {
            window.history.replaceState({}, "", window.location.pathname);
            const messaggi = {
                oauth_email_mismatch:
                    "L'account Microsoft selezionato non corrisponde all'email del tuo utente. Ripeti il collegamento scegliendo il tuo account.",
            };
            setCalendarError(messaggi[errorParam] || `Errore OAuth: ${errorParam.replace(/_/g, " ")}`);
        }

        loadCalendarStatus();
        loadDrafts();
        loadCount();
    }, [loadCalendarStatus, loadDrafts, loadCount]); // eslint-disable-line react-hooks/exhaustive-deps

    // Deep-link ?call=<id>: legge direttamente la scheda per id (funziona anche per
    // call in archivio oltre la prima pagina). Se non visibile (non owner/viewer o
    // id inesistente) mostra un avviso.
    const apriDeepLink = async (callId) => {
        const id = Number(callId);
        if (!Number.isInteger(id) || id <= 0) {
            setDeepLinkError("Link alla call non valido.");
            return;
        }
        const rec = await loadDetail(id);
        if (!rec) {
            setDeepLinkError("La call richiesta non è stata trovata, oppure non hai accesso a questa registrazione.");
            return;
        }
        setActiveTab(rec.stato_pipeline === "pubblicata" ? "archivio" : "revisione");
        apriRevisione(rec, { giaCaricata: true });
    };

    // Polling: 15 s con call in pipeline, 30 s altrimenti; sospeso con la tab del
    // browser nascosta; backoff esponenziale dopo errori consecutivi (max 5 min).
    // Chiede solo i conteggi e ricarica la lista quando cambiano.
    useEffect(() => {
        let timer = null;
        let cancelled = false;
        const tick = async () => {
            if (cancelled) return;
            if (!document.hidden) {
                const c = await loadCount();
                if (cancelled) return;
                if (c && (c.inPipeline || c.daRevisionare !== drafts.length)) await loadDrafts({ silent: true });
            }
            if (cancelled) return;
            const base = hasActiveBots ? POLL_MS_ATTIVO : POLL_MS;
            const delay = Math.min(base * 2 ** pollFailuresRef.current, POLL_BACKOFF_MAX_MS);
            timer = setTimeout(tick, delay);
        };
        timer = setTimeout(tick, hasActiveBots ? POLL_MS_ATTIVO : POLL_MS);
        const onVisible = () => { if (!document.hidden) { clearTimeout(timer); tick(); } };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            cancelled = true;
            clearTimeout(timer);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [hasActiveBots, drafts.length, loadCount, loadDrafts]);

    // Refresh immediato al cambio tab verso "revisione"; archivio caricato alla prima apertura.
    useEffect(() => {
        if (activeTab === "revisione") loadDrafts({ silent: drafts.length > 0 });
        if (activeTab === "archivio" && !archiveLoaded && !archiveLoading) loadArchive({ reset: true });
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── OAuth Microsoft ───────────────────────────────────────────────────────

    const collegaOutlook = async () => {
        setOauthLoading(true);
        try {
            const res = await axios.get(`${API_URL}/registrazioni/calendar/oauth/microsoft`, { headers: authHeader() });
            window.location.href = res.data.oauthUrl;
        } catch (err) {
            setCalendarError("Impossibile avviare il collegamento. Riprova.");
            setOauthLoading(false);
        }
    };

    const scollegaCalendario = async () => {
        try {
            await axios.delete(`${API_URL}/registrazioni/calendar`, { headers: authHeader() });
            setCalendarStatus({ connected: false });
            setMeetings([]);
        } catch (err) {
            console.error("scollegaCalendario:", err);
        }
    };

    // ── Toggle auto-record ────────────────────────────────────────────────────

    const toggleAutoRecord = async (eventId, currentValue) => {
        const nuovoValore = !currentValue;
        setMeetings((prev) => prev.map((m) => m.id === eventId ? { ...m, autoRecord: nuovoValore } : m));
        try {
            await axios.post(
                `${API_URL}/registrazioni/calendar/events/${eventId}/toggle`,
                { autoRecord: nuovoValore },
                { headers: authHeader() }
            );
        } catch (err) {
            // Rollback ottimistico
            setMeetings((prev) => prev.map((m) => m.id === eventId ? { ...m, autoRecord: currentValue } : m));
            console.error("toggleAutoRecord:", err);
        }
    };

    // Attiva/disattiva la sync su TUTTI i prossimi meeting in un colpo solo
    const setAllAutoRecord = async (value) => {
        // Niente guard su meetings.length: con il calendario vuoto la chiamata serve
        // comunque a impostare il default per i meeting che arriveranno.
        if (bulkLoading) return;
        setBulkLoading(true);
        const prev = meetings;
        setMeetings((ms) => ms.map((m) => ({ ...m, autoRecord: value })));
        try {
            await axios.post(
                `${API_URL}/registrazioni/calendar/events/toggle-all`,
                { autoRecord: value },
                { headers: authHeader() }
            );
            // Il toggle massivo vale anche come default per i meeting non ancora in
            // calendario: aggiorno lo stato locale così l'avviso qui sotto resta
            // coerente senza rifare la GET /calendar/status.
            setCalendarStatus((s) => (s ? { ...s, autoRecordDefault: value } : s));
            // Riallinea con il server (es. meeting già in registrazione non cambiano)
            await loadCalendarEvents();
        } catch (err) {
            setMeetings(prev); // rollback ottimistico
            console.error("setAllAutoRecord:", err);
        } finally {
            setBulkLoading(false);
        }
    };

    // ── Registra da link ──────────────────────────────────────────────────────

    const registraDaLink = async () => {
        if (!linkInput.trim()) return;
        setLinkLoading(true);
        setLinkSuccess(null);
        try {
            await axios.post(`${API_URL}/registrazioni/bot`, { meetingUrl: linkInput.trim() }, { headers: authHeader() });
            setLinkSuccess("Bot avviato — entrerà nella call e la registrazione comparirà nel tab \"Da revisionare\" a fine call.");
            setLinkInput("");
            setHasActiveBots(true);
        } catch (err) {
            setLinkSuccess("Errore: " + (err.response?.data?.error || err.message));
        } finally {
            setLinkLoading(false);
        }
    };

    // ── Da revisionare ────────────────────────────────────────────────────────

    const apriRevisione = async (draft, { giaCaricata = false } = {}) => {
        // I prossimi set* caricano i valori dal DB: non devono innescare l'autosave.
        skipSintesiSaveRef.current = true;
        setSelectedRecordingId(draft.id);
        setEditRiassunto(draft.sintesi_riassunto || "");
        setEditTitolo(draft.titolo || "Meeting registrato");
        setEditSchema(draft.sintesi_schema || []);
        setEditActionItems(draft.sintesi_action_items || []);
        setPublishError(null);
        setDetailError(null);
        if (giaCaricata) {
            setDetail(draft);
        } else {
            setDetail(null);
            // Trascrizione (e sintesi aggiornata) on demand. Se la sintesi sul server
            // differisce da quella in lista (es. modificata da un altro revisore),
            // riallinea gli edit senza innescare l'autosave.
            loadDetail(draft.id).then((full) => {
                if (!full) return;
                const stessa = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
                if (!stessa(full.sintesi_riassunto || "", draft.sintesi_riassunto || "")
                    || !stessa(full.sintesi_schema || [], draft.sintesi_schema || [])
                    || !stessa(full.sintesi_action_items || [], draft.sintesi_action_items || [])) {
                    skipSintesiSaveRef.current = true;
                    setEditRiassunto(full.sintesi_riassunto || "");
                    setEditSchema(full.sintesi_schema || []);
                    setEditActionItems(full.sintesi_action_items || []);
                }
            });
        }
        setShareSearch("");
        setShareError(null);
        setMediaVideoUrl(null);
        setMediaAudioUrl(null);
        setMediaLoading(true);
        // Carica gli utenti Interacta associati finché la call è da revisionare:
        // servono all'owner per la pubblicazione e a chiunque veda la scheda per condividerla.
        if (draft.stato_pipeline !== "pubblicata") {
            setLoadingInteractaUsers(true);
            axios.get(`${API_URL}/auth/interacta-users?soloAssociati=true`, { headers: authHeader() })
                .then((res) => setInteractaUsersForPublish(res.data))
                .catch(() => setInteractaUsersForPublish([]))
                .finally(() => setLoadingInteractaUsers(false));
            // Rubrica completa per i suggerimenti dell'owner negli action item
            axios.get(`${API_URL}/auth/interacta-users`, { headers: authHeader() })
                .then((res) => setInteractaUsersAll(res.data))
                .catch(() => setInteractaUsersAll([]));
        }
        try {
            const res = await axios.get(`${API_URL}/registrazioni/${draft.id}/media`, { headers: authHeader() });
            const video = res.data.videoUrl || null;
            const audio = res.data.audioUrl || null;
            setMediaVideoUrl(video);
            setMediaAudioUrl(audio);
            setMediaMode(video ? "video" : "audio"); // default al video se disponibile
        } catch {
            setMediaVideoUrl(null);
            setMediaAudioUrl(null);
        } finally {
            setMediaLoading(false);
        }
    };

    // Persiste subito riassunto/schema/action item della scheda aperta. Usata sia
    // dall'autosave debounced sia come flush prima di pubblicare su Interacta (che
    // rilegge la sintesi dal DB): così gli edit manuali non vengono mai ignorati.
    const salvaSintesiOra = useCallback(async () => {
        if (!selectedRecordingId || isReadOnly) return;
        if (sintesiSaveTimer.current) {
            clearTimeout(sintesiSaveTimer.current);
            sintesiSaveTimer.current = null;
        }
        try {
            await axios.patch(
                `${API_URL}/registrazioni/${selectedRecordingId}/sintesi`,
                { sintesi_riassunto: editRiassunto, sintesi_schema: editSchema, sintesi_action_items: editActionItems },
                { headers: authHeader() }
            );
            // Allinea la copia locale così un successivo reload non "riporta indietro" gli edit.
            const aggiorna = (r) => (r && r.id === selectedRecordingId
                ? { ...r, sintesi_riassunto: editRiassunto, sintesi_schema: editSchema, sintesi_action_items: editActionItems }
                : r);
            setDrafts((prev) => prev.map(aggiorna));
            setDetail(aggiorna);
        } catch (err) {
            console.error("salvaSintesi:", err);
        }
    }, [selectedRecordingId, isReadOnly, editRiassunto, editSchema, editActionItems]);

    // Autosave con debounce: ogni modifica agli edit riprogramma il salvataggio.
    useEffect(() => {
        if (skipSintesiSaveRef.current) { skipSintesiSaveRef.current = false; return; }
        if (isReadOnly || !selectedRecordingId) return;
        if (sintesiSaveTimer.current) clearTimeout(sintesiSaveTimer.current);
        sintesiSaveTimer.current = setTimeout(() => { salvaSintesiOra(); }, 800);
        return () => { if (sintesiSaveTimer.current) clearTimeout(sintesiSaveTimer.current); };
    }, [editRiassunto, editSchema, editActionItems]); // eslint-disable-line react-hooks/exhaustive-deps

    const chiudiRevisione = () => {
        // Flush di un eventuale edit ancora nel debounce prima di lasciare la scheda.
        salvaSintesiOra();
        setSelectedRecordingId(null);
        setDetail(null);
        setDetailError(null);
        setMediaVideoUrl(null);
        setMediaAudioUrl(null);
        setPublishError(null);
    };

    const archiviaRegistrazione = async () => {
        if (!selectedDraft) return;
        try {
            await axios.post(
                `${API_URL}/registrazioni/${selectedDraft.id}/archivia`,
                { sintesi_riassunto: editRiassunto, sintesi_schema: editSchema, sintesi_action_items: editActionItems },
                { headers: authHeader() }
            );
            await refreshLists();
            setSelectedRecordingId(null);
            setActiveTab("archivio");
        } catch (err) {
            console.error("archiviaRegistrazione:", err);
        }
    };

    const riportaInRevisione = async () => {
        if (!selectedDraft) return;
        try {
            await axios.post(
                `${API_URL}/registrazioni/${selectedDraft.id}/riporta-in-revisione`,
                {},
                { headers: authHeader() }
            );
            await refreshLists();
            setSelectedRecordingId(null);
            setActiveTab("revisione");
        } catch (err) {
            console.error("riportaInRevisione:", err);
        }
    };

    const eliminaRegistrazione = async () => {
        if (!selectedDraft || selectedDraft.interacta_post_id) return;
        if (!window.confirm(
            "Eliminare definitivamente questa registrazione?\n\n" +
            "L'operazione è irreversibile: verranno rimossi trascrizione, sintesi e chat associate. " +
            "Il file audio/video su Recall non viene toccato."
        )) return;
        try {
            await axios.delete(`${API_URL}/registrazioni/${selectedDraft.id}`, { headers: authHeader() });
            await refreshLists();
            setSelectedRecordingId(null);
        } catch (err) {
            console.error("eliminaRegistrazione:", err);
            alert(err.response?.data?.error || "Errore durante l'eliminazione della registrazione");
        }
    };

    const eliminaDaInteracta = async () => {
        if (!selectedDraft || !window.confirm("Eliminare i post Interacta (Task e Meeting) associati a questa registrazione?")) return;
        setPublishing(true);
        setPublishError(null);
        try {
            await axios.delete(`${API_URL}/registrazioni/${selectedDraft.id}/pubblica`, { headers: authHeader() });
            await refreshLists();
        } catch (err) {
            setPublishError(err.response?.data?.error || "Errore durante l'eliminazione.");
        } finally {
            setPublishing(false);
        }
    };

    const pubblicaSuInteracta = async () => {
        if (!selectedDraft) return;
        setPublishing(true);
        setPublishError(null);
        try {
            // Flush degli edit ancora nel debounce: il backend rilegge la sintesi dal DB.
            await salvaSintesiOra();
            await axios.post(
                `${API_URL}/registrazioni/${selectedDraft.id}/pubblica`,
                {},
                { headers: authHeader() }
            );
            await refreshLists();
        } catch (err) {
            setPublishError(err.response?.data?.error || "Errore durante la pubblicazione.");
        } finally {
            setPublishing(false);
        }
    };

    // ── Condivisione revisione ────────────────────────────────────────────────

    // Risolve il nome da mostrare per un viewer (username webapp da Gestione Utenze).
    const nomeDaWebappId = (webappUserId) => {
        const u = interactaUsersForPublish.find((x) => x.webappUserId === webappUserId);
        return u?.webappUsername || `Utente #${webappUserId}`;
    };

    const addViewer = async (webappUserId) => {
        if (!selectedDraft || !webappUserId) return;
        setAddingViewer(true);
        setShareError(null);
        const prev = drafts;
        setDrafts((ds) => ds.map((d) => d.id === selectedDraft.id
            ? { ...d, viewer_ids: [...(d.viewer_ids || []), webappUserId] } : d));
        try {
            const res = await axios.post(
                `${API_URL}/registrazioni/${selectedDraft.id}/viewers`,
                { userId: webappUserId },
                { headers: authHeader() }
            );
            if (res.data?.warning) setShareError(res.data.warning);
            await refreshLists();
            loadDetail(selectedDraft.id); // viewer_ids aggiornati anche nella scheda aperta
        } catch (err) {
            setDrafts(prev); // rollback ottimistico
            setShareError(err.response?.data?.error || "Errore durante l'aggiunta del revisore.");
        } finally {
            setAddingViewer(false);
        }
    };

    const removeViewer = async (webappUserId) => {
        if (!selectedDraft) return;
        setShareError(null);
        const prev = drafts;
        setDrafts((ds) => ds.map((d) => d.id === selectedDraft.id
            ? { ...d, viewer_ids: (d.viewer_ids || []).filter((v) => v !== webappUserId) } : d));
        try {
            const res = await axios.delete(
                `${API_URL}/registrazioni/${selectedDraft.id}/viewers/${webappUserId}`,
                { headers: authHeader() }
            );
            if (res.data?.warning) setShareError(res.data.warning);
            await refreshLists();
            loadDetail(selectedDraft.id); // viewer_ids aggiornati anche nella scheda aperta
        } catch (err) {
            setDrafts(prev); // rollback ottimistico
            setShareError(err.response?.data?.error || "Errore durante la rimozione del revisore.");
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    // ── Chatbot "Chiedi alla call" ────────────────────────────────────────────
    const seekTo = (sec) => {
        const el = mediaRef.current;
        if (!el || !isFinite(sec)) return;
        try { el.currentTime = sec; const p = el.play?.(); if (p?.catch) p.catch(() => {}); } catch (_) {}
        el.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    };

    const tsToSec = (str) => {
        const p = String(str).split(":").map(Number);
        if (p.some(isNaN)) return null;
        if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
        if (p.length === 2) return p[0] * 60 + p[1];
        return p[0];
    };

    // Rende cliccabili i timestamp [mm:ss] nel testo dell'assistente → seek del player.
    const renderWithTimestamps = (text) => {
        const parts = String(text || "").split(/(\d{1,2}:\d{2}(?::\d{2})?)/g);
        return parts.map((part, i) => {
            if (i % 2 === 1) {
                const sec = tsToSec(part);
                return (
                    <button key={i} type="button" className="rc-ts-chip" onClick={() => seekTo(sec)} title="Vai a questo momento">
                        {part}
                    </button>
                );
            }
            return <span key={i}>{part}</span>;
        });
    };

    const updateLastMsg = (fn) => setChatMessages((prev) => {
        if (prev.length === 0) return prev;
        const next = prev.slice();
        next[next.length - 1] = fn(next[next.length - 1]);
        return next;
    });

    const sendChat = async () => {
        const q = chatInput.trim();
        if (!q || chatStreaming || !selectedRecordingId) return;
        setChatInput("");
        setChatMessages((prev) => [
            ...prev,
            { role: "user", content: q },
            { role: "assistant", content: "", citations: [], streaming: true, listening: null },
        ]);
        setChatStreaming(true);
        try {
            const resp = await fetch(`${API_URL}/registrazioni/${selectedRecordingId}/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
                body: JSON.stringify({ message: q }),
            });
            if (!resp.ok || !resp.body) {
                let msg = "Errore nella richiesta";
                try { msg = (await resp.json()).error || msg; } catch (_) {}
                throw new Error(msg);
            }
            const reader = resp.body.getReader();
            const dec = new TextDecoder();
            let buf = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += dec.decode(value, { stream: true });
                const chunks = buf.split("\n\n");
                buf = chunks.pop();
                for (const chunk of chunks) {
                    const line = chunk.trim();
                    if (!line.startsWith("data:")) continue;
                    let ev;
                    try { ev = JSON.parse(line.slice(5).trim()); } catch (_) { continue; }
                    if (ev.type === "token") {
                        updateLastMsg((m) => ({ ...m, content: m.content + ev.data, listening: null }));
                    } else if (ev.type === "listening") {
                        updateLastMsg((m) => ({ ...m, listening: ev.window }));
                    } else if (ev.type === "done") {
                        updateLastMsg((m) => ({ ...m, citations: ev.citations || [], streaming: false, listening: null }));
                    } else if (ev.type === "error") {
                        updateLastMsg((m) => ({ ...m, content: (m.content ? m.content + "\n\n" : "") + `⚠️ ${ev.data}`, streaming: false, listening: null }));
                    }
                }
            }
        } catch (e) {
            updateLastMsg((m) => ({ ...m, content: (m.content ? m.content + "\n\n" : "") + `⚠️ ${e.message}`, streaming: false, listening: null }));
        } finally {
            setChatStreaming(false);
        }
    };

    // Carica la storia chat all'apertura di una call; azzera alla chiusura.
    useEffect(() => {
        if (!selectedRecordingId) { setChatMessages([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API_URL}/registrazioni/${selectedRecordingId}/chat`, { headers: authHeader() });
                if (!cancelled) setChatMessages((res.data.messages || []).map((m) => ({ role: m.role, content: m.content, citations: m.citations || [] })));
            } catch (_) { if (!cancelled) setChatMessages([]); }
        })();
        return () => { cancelled = true; };
    }, [selectedRecordingId]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [chatMessages]);

    const TABS = [
        { key: "calendario", label: "Calendario", icon: Calendar },
        { key: "revisione", label: "Da revisionare", icon: Inbox, badge: drafts.length },
        { key: "archivio", label: "Archivio", icon: Archive },
    ];

    return (
        <div className="placeholder-container rc-container">
            <header className="page-header">
                <div className="header-content">
                    <div className="header-title">
                        <h2>Registrazioni &amp; Sintesi Call</h2>
                        <p className="header-subtitle">
                            Registra videocall e riunioni, ottieni trascrizione e sintesi AI, pubblica su Interacta dopo la revisione
                        </p>
                    </div>
                </div>
            </header>

            <div className="content-area rc-content">
                {/* Avviso deep-link non risolto (call inesistente o senza accesso) */}
                {deepLinkError && (
                    <div className="rc-proto-banner rc-error-banner">
                        <AlertCircle size={16} /> {deepLinkError}
                        <button className="rc-icon-btn" style={{ marginLeft: "auto" }} onClick={() => setDeepLinkError(null)} title="Chiudi">
                            <X size={14} />
                        </button>
                    </div>
                )}

                {/* Tabs */}
                <nav className="rc-tabs" role="tablist">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        return (
                            <button
                                key={t.key}
                                role="tab"
                                aria-selected={activeTab === t.key}
                                className={`rc-tab ${activeTab === t.key ? "rc-tab-active" : ""}`}
                                onClick={() => { setActiveTab(t.key); setSelectedRecordingId(null); setDetail(null); }}
                            >
                                <Icon size={16} />
                                {t.label}
                                {t.badge > 0 && <span className="rc-tab-badge">{t.badge}</span>}
                            </button>
                        );
                    })}
                </nav>

                {/* ============ TAB CALENDARIO ============ */}
                {activeTab === "calendario" && (
                    <div className="rc-panel">
                        {calendarError && (
                            <div className="rc-proto-banner rc-error-banner">
                                <AlertCircle size={16} /> {calendarError}
                            </div>
                        )}

                        {/* Stato collegamento calendario */}
                        <div className="rc-card">
                            {calendarLoading ? (
                                <div className="rc-loading"><Loader size={20} className="rc-spin" /> Caricamento stato calendario…</div>
                            ) : calendarStatus?.connected ? (
                                <div className="rc-connect">
                                    <div className="rc-connect-info">
                                        <div className="rc-connect-icon ok"><Calendar size={22} /></div>
                                        <div>
                                            <h3 className="rc-card-title">Outlook / Microsoft 365</h3>
                                            <p className="rc-muted">
                                                Collegato come <strong>{calendarStatus.email}</strong> — i meeting con link videocall vengono sincronizzati automaticamente.
                                            </p>
                                        </div>
                                    </div>
                                    <button className="rc-btn rc-btn-ghost" onClick={scollegaCalendario}>
                                        <RefreshCw size={15} /> Scollega
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <h3 className="rc-card-title"><Calendar size={16} /> Collega il calendario</h3>
                                    <p className="rc-muted">
                                        Collega il tuo calendario Outlook / Microsoft 365 per attivare la registrazione automatica delle call Teams.
                                    </p>
                                    <div className="rc-provider-row">
                                        <button
                                            className="rc-provider-btn rc-provider-suggested"
                                            onClick={collegaOutlook}
                                            disabled={oauthLoading}
                                        >
                                            {oauthLoading ? <Loader size={16} className="rc-spin" /> : <Link2 size={16} />}
                                            <span>Collega Outlook / Microsoft 365</span>
                                            <span className="rc-provider-tag">consigliato</span>
                                        </button>
                                        <button className="rc-provider-btn" disabled title="Google Calendar — disponibile prossimamente">
                                            <Link2 size={16} />
                                            <span>Collega Google Calendar</span>
                                            <span className="rc-provider-tag rc-provider-tag-soon">presto</span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Registra adesso da link */}
                        <div className="rc-card">
                            <h3 className="rc-card-title"><Link2 size={16} /> Registra adesso da link</h3>
                            <p className="rc-muted">Incolla il link di una call in corso o programmata: il bot entrerà come partecipante e registrerà.</p>
                            <div className="rc-link-row">
                                <input
                                    className="rc-input"
                                    type="text"
                                    placeholder="https://teams.microsoft.com/... · meet.google.com/... · zoom.us/..."
                                    value={linkInput}
                                    onChange={(e) => { setLinkInput(e.target.value); setLinkSuccess(null); }}
                                />
                                <button className="rc-btn rc-btn-primary" onClick={registraDaLink} disabled={!linkInput.trim() || linkLoading}>
                                    {linkLoading ? <Loader size={15} className="rc-spin" /> : <Mic size={15} />} Registra
                                </button>
                            </div>
                            {linkSuccess && (
                                <p className={`rc-muted ${linkSuccess.startsWith("Errore") ? "rc-error-text" : "rc-success-text"}`} style={{ marginTop: 8 }}>
                                    {linkSuccess}
                                </p>
                            )}
                        </div>

                        {/* Prossimi meeting */}
                        <div className="rc-card">
                            <div className="rc-card-header-row">
                                <h3 className="rc-card-title"><Calendar size={16} /> Prossimi meeting</h3>
                                {calendarStatus?.connected && (
                                    <div className="rc-header-actions">
                                        {/* I due pulsanti valgono anche per i meeting non ancora in calendario,
                                            quindi restano cliccabili finché il default non è già quello richiesto:
                                            altrimenti chi ha la lista tutta spenta non potrebbe più impostarlo.
                                            .every() su lista vuota è true → con calendario vuoto si imposta solo
                                            il default, che è esattamente quello che serve. */}
                                        <button
                                            className="rc-btn rc-btn-ghost rc-btn-sm"
                                            onClick={() => setAllAutoRecord(true)}
                                            disabled={bulkLoading || meetingsLoading
                                                || (calendarStatus.autoRecordDefault !== false && meetings.every((m) => m.autoRecord))}
                                            title="Attiva la registrazione automatica su tutti i prossimi meeting, anche quelli futuri"
                                        >
                                            {bulkLoading ? <Loader size={13} className="rc-spin" /> : <CheckCircle2 size={13} />} Attiva tutti
                                        </button>
                                        <button
                                            className="rc-btn rc-btn-ghost rc-btn-sm"
                                            onClick={() => setAllAutoRecord(false)}
                                            disabled={bulkLoading || meetingsLoading
                                                || (calendarStatus.autoRecordDefault === false && meetings.every((m) => !m.autoRecord))}
                                            title="Disattiva la registrazione automatica su tutti i prossimi meeting, anche quelli futuri"
                                        >
                                            {bulkLoading ? <Loader size={13} className="rc-spin" /> : <X size={13} />} Disattiva tutti
                                        </button>
                                        <button className="rc-btn rc-btn-ghost rc-btn-sm" onClick={loadCalendarEvents} disabled={meetingsLoading || bulkLoading}>
                                            <RefreshCw size={14} className={meetingsLoading ? "rc-spin" : ""} />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {calendarStatus?.connected && calendarStatus.autoRecordDefault === false && (
                                <p className="rc-muted" style={{ marginTop: 4 }}>
                                    Anche i prossimi meeting che compariranno in calendario nasceranno con la registrazione disattivata.
                                </p>
                            )}

                            {!calendarStatus?.connected ? (
                                <div className="rc-empty">
                                    <Calendar size={32} />
                                    <p>Collega il calendario per vedere i tuoi meeting e scegliere quali registrare.</p>
                                </div>
                            ) : meetingsLoading ? (
                                <div className="rc-loading"><Loader size={20} className="rc-spin" /> Caricamento meeting…</div>
                            ) : meetings.length === 0 ? (
                                <div className="rc-empty">
                                    <Calendar size={32} />
                                    <p>Nessun meeting futuro con link videocall trovato nel calendario.</p>
                                </div>
                            ) : (
                                <ul className="rc-meeting-list">
                                    {meetings.map((m) => (
                                        <li key={m.id} className="rc-meeting">
                                            <div className="rc-meeting-main">
                                                <span className="rc-meeting-title">{m.titolo}</span>
                                                <div className="rc-meeting-meta">
                                                    <PlatformBadge platform={m.piattaforma || "other"} />
                                                    <span><Clock size={13} /> {fmtData(m.startTime)}</span>
                                                </div>
                                            </div>
                                            <label className="rc-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={m.autoRecord}
                                                    onChange={() => toggleAutoRecord(m.id, m.autoRecord)}
                                                />
                                                <span className="rc-toggle-track"><span className="rc-toggle-thumb" /></span>
                                                <span className="rc-toggle-label">{m.autoRecord ? "Registra" : "Ignora"}</span>
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {/* ============ TAB DA REVISIONARE + DETTAGLIO ARCHIVIO ============ */}
                {(activeTab === "revisione" || (activeTab === "archivio" && selectedDraft)) && (
                    <div className="rc-panel">
                        {!selectedDraft ? (
                            draftsLoading && drafts.length === 0 ? (
                                <div className="rc-loading rc-card"><Loader size={20} className="rc-spin" /> Caricamento registrazioni…</div>
                            ) : draftsError && drafts.length === 0 ? (
                                <div className="rc-empty rc-card rc-empty-error">
                                    <AlertCircle size={32} />
                                    <p>{draftsError}</p>
                                    <button className="rc-btn rc-btn-ghost rc-btn-sm" onClick={() => loadDrafts()}>
                                        <RefreshCw size={13} /> Riprova
                                    </button>
                                </div>
                            ) : drafts.length === 0 ? (
                                <div className="rc-empty rc-card">
                                    <Inbox size={32} />
                                    <p>Nessuna registrazione da revisionare. Le bozze appariranno qui a fine call.</p>
                                </div>
                            ) : (
                                <>
                                {draftsError && (
                                    <div className="rc-proto-banner rc-error-banner">
                                        <AlertCircle size={16} /> {draftsError}
                                        <button className="rc-btn rc-btn-ghost rc-btn-sm" style={{ marginLeft: "auto" }} onClick={() => loadDrafts()}>
                                            <RefreshCw size={13} /> Riprova
                                        </button>
                                    </div>
                                )}
                                {/* Filtro mie / sola lettura: chi partecipa a molte call vede anche le
                                    schede degli altri revisori e vuole isolare le proprie. */}
                                {filtroDisponibile && (
                                    <div className="rc-filter" role="tablist" aria-label="Filtra registrazioni">
                                        {[
                                            { key: "tutte", label: "Tutte", n: drafts.length },
                                            { key: "mie", label: "Da revisionare da me", n: draftsMie.length },
                                            { key: "sola_lettura", label: "Sola lettura", n: draftsSolaLettura.length },
                                        ].map((f) => (
                                            <button
                                                key={f.key}
                                                type="button"
                                                role="tab"
                                                aria-selected={draftsFilter === f.key}
                                                className={`rc-filter-btn ${draftsFilter === f.key ? "rc-filter-btn-active" : ""}`}
                                                onClick={() => setDraftsFilter(f.key)}
                                            >
                                                {f.label} <span className="rc-filter-count">{f.n}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {draftsVisibili.length === 0 && (
                                    <div className="rc-empty rc-card">
                                        <Inbox size={32} />
                                        <p>{draftsFilter === "mie"
                                            ? "Nessuna registrazione da revisionare come revisore."
                                            : "Nessuna registrazione in sola lettura."}</p>
                                    </div>
                                )}
                                <ul className="rc-draft-list">
                                    {draftsVisibili.map((d) => (
                                        <li key={d.id} className="rc-card rc-draft" onClick={() => apriRevisione(d)} role="button" tabIndex={0}>
                                            <div className="rc-draft-main">
                                                <span className="rc-draft-title">
                                                    {d.titolo || "Meeting registrato"}
                                                    {d.is_owner === false && <span className="rc-readonly-tag">Sola lettura</span>}
                                                </span>
                                                <div className="rc-meeting-meta">
                                                    <PlatformBadge platform={d.piattaforma || "other"} />
                                                    <span><Clock size={13} /> {fmtData(d.join_at || d.created_at)} · {fmtDurata(d.durata_sec ? Math.round(d.durata_sec / 60) : null)}</span>
                                                </div>
                                                <p className="rc-draft-preview">{d.sintesi_riassunto || d.anteprima || "Trascrizione disponibile — sintesi AI non ancora generata."}</p>
                                            </div>
                                            <div className="rc-draft-cta">Rivedi <ChevronLeft size={16} style={{ transform: "rotate(180deg)" }} /></div>
                                        </li>
                                    ))}
                                </ul>
                                </>
                            )
                        ) : (
                            <div className="rc-review">
                                <button className="rc-back" onClick={chiudiRevisione}>
                                    <ChevronLeft size={16} /> Torna all'elenco
                                </button>
                                {!isOwner && (
                                    <div className="rc-readonly-banner rc-card">
                                        <Users size={15} /> Sola lettura — sei tra i partecipanti di questa call.
                                        La revisione è di <strong>{selectedDraft.owner_username || "un altro utente"}</strong>,
                                        che è l'unico a poter pubblicare su Interacta.
                                    </div>
                                )}
                                <div className="rc-review-header">
                                    {!isReadOnly && (
                                        <button className="rc-btn rc-btn-primary rc-btn-archivia" onClick={archiviaRegistrazione}>
                                            <Archive size={15} /> Archivia
                                        </button>
                                    )}
                                    {!isReadOnly && (
                                        <button
                                            className="rc-btn rc-btn-danger-ghost rc-btn-archivia"
                                            onClick={eliminaRegistrazione}
                                            disabled={!!selectedDraft.interacta_post_id}
                                            title={selectedDraft.interacta_post_id
                                                ? "Rimuovi prima la pubblicazione da Interacta per poter eliminare la scheda."
                                                : "Elimina definitivamente questa registrazione"}
                                        >
                                            <Trash2 size={15} /> Elimina
                                        </button>
                                    )}
                                    {isArchived && isOwner && (
                                        <button className="rc-btn rc-btn-ghost rc-btn-archivia" onClick={riportaInRevisione} title="Riporta la call in 'Da revisionare' (i post Interacta restano)">
                                            <RefreshCw size={15} /> Riporta in revisione
                                        </button>
                                    )}
                                    <h2
                                        className={`rc-review-title${isReadOnly ? " rc-review-title-readonly" : ""}`}
                                        contentEditable={!isReadOnly}
                                        suppressContentEditableWarning
                                        onBlur={async (e) => {
                                            if (isReadOnly) return;
                                            const nuovo = e.currentTarget.textContent.trim();
                                            if (!nuovo || nuovo === editTitolo) return;
                                            setEditTitolo(nuovo);
                                            setDrafts((prev) => prev.map((d) => d.id === selectedDraft.id ? { ...d, titolo: nuovo } : d));
                                            setArchive((prev) => prev.map((a) => a.id === selectedDraft.id ? { ...a, titolo: nuovo } : a));
                                            setDetail((d) => (d && d.id === selectedDraft.id ? { ...d, titolo: nuovo } : d));
                                            try {
                                                await axios.patch(
                                                    `${API_URL}/registrazioni/${selectedDraft.id}/titolo`,
                                                    { titolo: nuovo },
                                                    { headers: authHeader() }
                                                );
                                            } catch (err) {
                                                console.error("aggiornaTitolo:", err);
                                            }
                                        }}
                                    >
                                        {editTitolo}
                                    </h2>
                                </div>
                                <div className="rc-review-grid">
                                    <div className="rc-review-col">
                                        <div className="rc-card">
                                            <h3 className="rc-card-title"><Play size={16} /> Registrazione</h3>
                                            {mediaLoading ? (
                                                <div className="rc-loading"><Loader size={16} className="rc-spin" /> Caricamento registrazione…</div>
                                            ) : (mediaVideoUrl || mediaAudioUrl) ? (
                                                <>
                                                    {mediaVideoUrl && mediaAudioUrl && (
                                                        <div className="rc-media-toggle">
                                                            <button
                                                                className={`rc-media-tab ${mediaMode === "video" ? "rc-media-tab-active" : ""}`}
                                                                onClick={() => setMediaMode("video")}
                                                            >
                                                                <Video size={13} /> Video
                                                            </button>
                                                            <button
                                                                className={`rc-media-tab ${mediaMode === "audio" ? "rc-media-tab-active" : ""}`}
                                                                onClick={() => setMediaMode("audio")}
                                                            >
                                                                <Mic size={13} /> Solo audio
                                                            </button>
                                                        </div>
                                                    )}
                                                    {mediaMode === "video" && mediaVideoUrl ? (
                                                        <video ref={mediaRef} controls src={mediaVideoUrl} className="rc-video" />
                                                    ) : (
                                                        <audio ref={mediaRef} controls src={mediaAudioUrl || mediaVideoUrl} style={{ width: "100%", marginBottom: 8 }} />
                                                    )}
                                                </>
                                            ) : (
                                                <p className="rc-muted">Registrazione non disponibile.</p>
                                            )}
                                            <div className="rc-meeting-meta" style={{ marginTop: 8 }}>
                                                <PlatformBadge platform={selectedDraft.piattaforma || "other"} />
                                                <span><Clock size={13} /> {fmtData(selectedDraft.join_at || selectedDraft.created_at)}</span>
                                                {selectedDraft.durata_sec && <span>{fmtDurata(Math.round(selectedDraft.durata_sec / 60))}</span>}
                                                {selectedDraft.partecipanti?.length > 0 && (
                                                    <span><Users size={13} /> {selectedDraft.partecipanti.join(", ")}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="rc-card">
                                            <h3 className="rc-card-title"><FileText size={16} /> Trascrizione</h3>
                                            <div className="rc-transcript">
                                                {detailLoading && !selectedDraft.transcript_testo
                                                    ? <div className="rc-loading"><Loader size={16} className="rc-spin" /> Caricamento trascrizione…</div>
                                                    : detailError && !selectedDraft.transcript_testo
                                                        ? <p className="rc-muted rc-error-text">
                                                            {detailError}{" "}
                                                            <button className="rc-btn rc-btn-ghost rc-btn-sm" onClick={() => loadDetail(selectedDraft.id)}>
                                                                <RefreshCw size={13} /> Riprova
                                                            </button>
                                                          </p>
                                                        : selectedDraft.transcript_testo
                                                            ? selectedDraft.transcript_testo.split("\n").map((line, i) => (
                                                                <p key={i}>{line}</p>
                                                            ))
                                                            : <p className="rc-muted">Trascrizione non ancora disponibile.</p>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div className="rc-review-col">
                                        {/* Chi può vedere questa call — condivisione (owner + viewer, solo da revisionare) */}
                                        {!isArchived && (
                                            <div className="rc-card rc-share">
                                                <h3 className="rc-card-title"><Users size={16} /> Chi può vedere questa call</h3>
                                                <ul className="rc-access-list">
                                                    <li className="rc-access-item">
                                                        <span>{selectedDraft.owner_username || "Owner"}</span>
                                                        <span className="rc-access-tag">Revisore</span>
                                                    </li>
                                                    {(selectedDraft.viewer_ids || []).map((vid) => (
                                                        <li key={vid} className="rc-access-item">
                                                            <span>{nomeDaWebappId(vid)}</span>
                                                            <div className="rc-access-item-right">
                                                                <span className="rc-access-tag rc-access-tag-viewer">Sola lettura</span>
                                                                <button className="rc-icon-btn" title="Rimuovi accesso" onClick={() => removeViewer(vid)}>
                                                                    <X size={14} />
                                                                </button>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>

                                                <p className="rc-muted" style={{ marginTop: 10, marginBottom: 6 }}>
                                                    Aggiungi un revisore in sola lettura (utenti con account Interacta associato):
                                                </p>
                                                {loadingInteractaUsers ? (
                                                    <div className="rc-loading"><Loader size={16} className="rc-spin" /> Caricamento utenti…</div>
                                                ) : (
                                                    <>
                                                        <input
                                                            type="text"
                                                            className="rc-notify-search"
                                                            placeholder="Cerca utente…"
                                                            value={shareSearch}
                                                            onChange={(e) => setShareSearch(e.target.value)}
                                                        />
                                                        <ul className="rc-notify-list">
                                                            {interactaUsersForPublish
                                                                .filter((u) => u.webappUserId
                                                                    && u.webappUserId !== selectedDraft.user_id
                                                                    && !(selectedDraft.viewer_ids || []).includes(u.webappUserId))
                                                                .filter((u) => {
                                                                    const q = shareSearch.toLowerCase();
                                                                    return !q || (u.webappUsername || "").toLowerCase().includes(q);
                                                                })
                                                                .map((u) => (
                                                                    <li key={u.id} className="rc-notify-item rc-access-add">
                                                                        <span>{u.webappUsername}</span>
                                                                        <button
                                                                            className="rc-btn rc-btn-ghost rc-btn-sm"
                                                                            disabled={addingViewer}
                                                                            onClick={() => addViewer(u.webappUserId)}
                                                                        >
                                                                            <Plus size={13} /> Aggiungi
                                                                        </button>
                                                                    </li>
                                                                ))}
                                                        </ul>
                                                    </>
                                                )}
                                                {shareError && (
                                                    <p className="rc-muted rc-error-text" style={{ marginTop: 8 }}>{shareError}</p>
                                                )}
                                            </div>
                                        )}

                                        {/* Riassunto */}
                                        <div className="rc-card">
                                            <h3 className="rc-card-title">
                                                <FileText size={16} /> Riassunto
                                                {selectedDraft.sintesi_riassunto && !isReadOnly && <span className="rc-ai-pill">AI · editabile</span>}
                                            </h3>
                                            {selectedDraft.sintesi_riassunto
                                                ? isReadOnly
                                                    ? <p className="rc-text">{selectedDraft.sintesi_riassunto}</p>
                                                    : <textarea className="rc-textarea" rows={6} value={editRiassunto} onChange={(e) => setEditRiassunto(e.target.value)} />
                                                : <p className="rc-muted">Sintesi AI non ancora disponibile.</p>
                                            }
                                        </div>

                                        {/* Schema dei punti */}
                                        <div className="rc-card">
                                            <h3 className="rc-card-title">
                                                <ListChecks size={16} /> Schema dei punti
                                                {editSchema.length > 0 && !isReadOnly && <span className="rc-ai-pill">AI · editabile</span>}
                                            </h3>
                                            {isReadOnly ? (
                                                selectedDraft.sintesi_schema?.length
                                                    ? <ul className="rc-bullets">{selectedDraft.sintesi_schema.map((s, i) => <li key={i}>{s}</li>)}</ul>
                                                    : <p className="rc-muted">Non disponibile.</p>
                                            ) : (
                                                <>
                                                    {editSchema.length === 0 && <p className="rc-muted">Non ancora disponibile.</p>}
                                                    <ul className="rc-schema-edit">
                                                        {editSchema.map((punto, i) => (
                                                            <li key={i} className="rc-schema-item">
                                                                <input
                                                                    className="rc-input rc-input-inline"
                                                                    value={punto}
                                                                    onChange={(e) => setEditSchema((prev) => prev.map((p, j) => j === i ? e.target.value : p))}
                                                                />
                                                                <button className="rc-icon-btn" onClick={() => setEditSchema((prev) => prev.filter((_, j) => j !== i))}><X size={14} /></button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <button className="rc-btn rc-btn-ghost rc-btn-sm" style={{ marginTop: 6 }} onClick={() => setEditSchema((prev) => [...prev, ""])}>
                                                        <Plus size={13} /> Aggiungi punto
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                        {/* Action items */}
                                        <div className="rc-card">
                                            <h3 className="rc-card-title">
                                                <CheckCircle2 size={16} /> Action item
                                                {editActionItems.length > 0 && !isReadOnly && <span className="rc-ai-pill">AI · editabile</span>}
                                            </h3>
                                            {isReadOnly ? (
                                                selectedDraft.sintesi_action_items?.length
                                                    ? <ul className="rc-actions-list">
                                                        {selectedDraft.sintesi_action_items.map((a, i) => (
                                                            <li key={i}>
                                                                <span className="rc-action-text">{a.testo || a.text || a}</span>
                                                                {a.owner && <span className="rc-action-meta">{a.owner}{a.scadenza ? ` · ${a.scadenza}` : ""}</span>}
                                                            </li>
                                                        ))}
                                                      </ul>
                                                    : <p className="rc-muted">Non disponibile.</p>
                                            ) : (
                                                <>
                                                    {editActionItems.length === 0 && <p className="rc-muted">Non ancora disponibili.</p>}
                                                    <ul className="rc-actions-edit">
                                                        {editActionItems.map((item, i) => {
                                                            const ownerQuery = (item.owner || "").toLowerCase();
                                                            const ownerMatches = ownerComboOpen === i
                                                                ? interactaUsersAll
                                                                    .filter((u) => !ownerQuery || `${u.lastName} ${u.firstName}`.toLowerCase().includes(ownerQuery))
                                                                    .slice(0, 8)
                                                                : [];
                                                            return (
                                                                <li key={i} className="rc-action-item-edit">
                                                                    <input
                                                                        className="rc-input rc-input-inline rc-input-grow"
                                                                        placeholder="Azione…"
                                                                        value={item.testo || ""}
                                                                        onChange={(e) => setEditActionItems((prev) => prev.map((a, j) => j === i ? { ...a, testo: e.target.value } : a))}
                                                                    />
                                                                    <div className="rc-owner-combo">
                                                                        <input
                                                                            className="rc-input rc-input-inline"
                                                                            placeholder="Owner"
                                                                            value={item.owner || ""}
                                                                            onFocus={() => setOwnerComboOpen(i)}
                                                                            onChange={(e) => { setEditActionItems((prev) => prev.map((a, j) => j === i ? { ...a, owner: e.target.value } : a)); setOwnerComboOpen(i); }}
                                                                            onBlur={() => setTimeout(() => setOwnerComboOpen((cur) => cur === i ? null : cur), 120)}
                                                                        />
                                                                        {ownerMatches.length > 0 && (
                                                                            <ul className="rc-owner-options">
                                                                                {ownerMatches.map((u) => {
                                                                                    const nome = `${u.lastName} ${u.firstName}`;
                                                                                    return (
                                                                                        <li
                                                                                            key={u.id}
                                                                                            className="rc-owner-option"
                                                                                            onMouseDown={(e) => {
                                                                                                e.preventDefault();
                                                                                                setEditActionItems((prev) => prev.map((a, j) => j === i ? { ...a, owner: nome } : a));
                                                                                                setOwnerComboOpen(null);
                                                                                            }}
                                                                                        >
                                                                                            {nome}
                                                                                        </li>
                                                                                    );
                                                                                })}
                                                                            </ul>
                                                                        )}
                                                                    </div>
                                                                    <input
                                                                        className="rc-input rc-input-inline rc-input-date"
                                                                        placeholder="Scadenza"
                                                                        value={item.scadenza || ""}
                                                                        onChange={(e) => setEditActionItems((prev) => prev.map((a, j) => j === i ? { ...a, scadenza: e.target.value } : a))}
                                                                    />
                                                                    <button className="rc-icon-btn" onClick={() => setEditActionItems((prev) => prev.filter((_, j) => j !== i))}><X size={14} /></button>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                    <button className="rc-btn rc-btn-ghost rc-btn-sm" style={{ marginTop: 6 }} onClick={() => setEditActionItems((prev) => [...prev, { testo: "", owner: "", scadenza: "" }])}>
                                                        <Plus size={13} /> Aggiungi action item
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                        {/* Azioni — sola lettura (archivio o viewer) vs revisione owner */}
                                        {isReadOnly ? (
                                            <div className="rc-card rc-publish">
                                                <h3 className="rc-card-title"><Send size={16} /> Pubblicazione Interacta</h3>
                                                {selectedDraft.interacta_post_id
                                                    ? <p className="rc-text rc-text-success">Pubblicato su Interacta (post Meeting #{selectedDraft.interacta_post_id}).</p>
                                                    : <p className="rc-muted">{isOwner ? "Non pubblicato su Interacta." : "Non ancora pubblicato su Interacta dall'owner."}</p>
                                                }
                                            </div>
                                        ) : (
                                            <div className="rc-card rc-publish">
                                                <h3 className="rc-card-title"><Send size={16} /> Pubblica su Interacta <span className="rc-muted" style={{ fontWeight: 400, fontSize: "0.8em" }}>(opzionale)</span></h3>
                                                {selectedDraft.interacta_post_id ? (
                                                    <div className="rc-publish-done">
                                                        <p className="rc-text rc-text-success">Pubblicato su Interacta (post Meeting #{selectedDraft.interacta_post_id}).</p>
                                                        <button className="rc-btn rc-btn-danger-ghost rc-btn-sm" onClick={eliminaDaInteracta} disabled={publishing}>
                                                            <Trash2 size={14} /> Elimina da Interacta
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <p className="rc-muted" style={{ marginBottom: 8 }}>
                                                            Verranno notificati su Interacta gli utenti elencati sopra in <strong>“Chi può vedere questa call”</strong> (owner e revisori con account completo).
                                                        </p>
                                                        {publishError && (
                                                            <p className="rc-muted rc-error-text" style={{ marginTop: 8 }}>{publishError}</p>
                                                        )}
                                                        <div className="rc-publish-actions">
                                                            <button className="rc-btn rc-btn-primary" onClick={pubblicaSuInteracta} disabled={publishing}>
                                                                {publishing ? <><Loader size={15} className="rc-spin" /> Pubblicazione…</> : <><Send size={15} /> Pubblica su Interacta</>}
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* ============ CHAT "Chiedi alla call" ============ */}
                                <div className="rc-card rc-chat">
                                    <h3 className="rc-card-title"><Sparkles size={16} /> Chiedi alla call</h3>
                                    <p className="rc-muted rc-chat-hint">
                                        Fai domande sui temi della call: l'assistente ascolta l'audio reale e cita i momenti — clicca un timestamp per saltarci nel player.
                                    </p>
                                    <div className="rc-chat-messages">
                                        {chatMessages.length === 0 && (
                                            <p className="rc-muted">Nessun messaggio. Prova con "Di cosa si è parlato?" o "Quali decisioni sono state prese?"</p>
                                        )}
                                        {chatMessages.map((m, i) => (
                                            <div key={i} className={`rc-chat-msg rc-chat-${m.role}`}>
                                                <div className="rc-chat-bubble">
                                                    {m.role === "assistant" ? (
                                                        <>
                                                            {m.content && <div className="rc-chat-text">{renderWithTimestamps(m.content)}</div>}
                                                            {!m.streaming && m.citations?.length > 0 && (
                                                                <div className="rc-chat-cites">
                                                                    <span className="rc-chat-cites-label">Momenti:</span>
                                                                    {m.citations.map((c, ci) => (
                                                                        <button
                                                                            key={ci}
                                                                            type="button"
                                                                            className="rc-ts-chip"
                                                                            onClick={() => seekTo(c.t_start)}
                                                                            title={c.quote || "Vai a questo momento"}
                                                                        >
                                                                            {c.label || `${Math.floor(c.t_start / 60)}:${String(Math.round(c.t_start % 60)).padStart(2, "0")}`}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {m.streaming && m.listening && (
                                                                <div className="rc-chat-listening">
                                                                    <Volume2 size={13} className="rc-spin-slow" /> Sto ascoltando l'audio a {`${Math.floor(m.listening.t_start / 60)}:${String(Math.round(m.listening.t_start % 60)).padStart(2, "0")}`}…
                                                                </div>
                                                            )}
                                                            {m.streaming && !m.content && !m.listening && (
                                                                <div className="rc-chat-typing"><Loader size={13} className="rc-spin" /></div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="rc-chat-text">{m.content}</div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        <div ref={chatEndRef} />
                                    </div>
                                    <div className="rc-chat-input">
                                        <input
                                            type="text"
                                            placeholder="Scrivi una domanda sulla call…"
                                            value={chatInput}
                                            onChange={(e) => setChatInput(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
                                            disabled={chatStreaming}
                                        />
                                        <button className="rc-btn rc-btn-primary" onClick={sendChat} disabled={chatStreaming || !chatInput.trim()}>
                                            {chatStreaming ? <Loader size={15} className="rc-spin" /> : <Send size={15} />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ============ TAB ARCHIVIO ============ */}
                {activeTab === "archivio" && !selectedDraft && (
                    <div className="rc-panel">
                        {archiveLoading && archive.length === 0 ? (
                            <div className="rc-loading rc-card"><Loader size={20} className="rc-spin" /> Caricamento archivio…</div>
                        ) : archiveError && archive.length === 0 ? (
                            <div className="rc-empty rc-card rc-empty-error">
                                <AlertCircle size={32} />
                                <p>{archiveError}</p>
                                <button className="rc-btn rc-btn-ghost rc-btn-sm" onClick={() => loadArchive({ reset: true })}>
                                    <RefreshCw size={13} /> Riprova
                                </button>
                            </div>
                        ) : archive.length === 0 ? (
                            <div className="rc-empty rc-card">
                                <Archive size={32} />
                                <p>Nessuna registrazione archiviata. Quelle che approvi compaiono qui.</p>
                            </div>
                        ) : (
                            <>
                            <ul className="rc-draft-list">
                                {archive.map((a) => (
                                    <li key={a.id} className="rc-card rc-draft" onClick={() => apriRevisione(a)} role="button" tabIndex={0}>
                                        <div className="rc-draft-main">
                                            <span className="rc-draft-title">{a.titolo || "Meeting registrato"}</span>
                                            <div className="rc-meeting-meta">
                                                <PlatformBadge platform={a.piattaforma || "other"} />
                                                <span><Clock size={13} /> {fmtData(a.join_at || a.created_at)}</span>
                                                {a.interacta_post_id && <span><ExternalLink size={13} /> Interacta</span>}
                                            </div>
                                        </div>
                                        <div className="rc-draft-cta">Vedi <ChevronLeft size={16} style={{ transform: "rotate(180deg)" }} /></div>
                                    </li>
                                ))}
                            </ul>
                            {archiveError && (
                                <p className="rc-muted rc-error-text" style={{ marginTop: 8 }}>{archiveError}</p>
                            )}
                            {archiveHasMore && (
                                <div className="rc-load-more">
                                    <button
                                        className="rc-btn rc-btn-ghost"
                                        disabled={archiveLoading}
                                        onClick={() => loadArchive({ reset: false, beforeId: archiveNextBeforeId })}
                                    >
                                        {archiveLoading ? <Loader size={14} className="rc-spin" /> : <ChevronLeft size={14} style={{ transform: "rotate(-90deg)" }} />} Carica altre
                                    </button>
                                </div>
                            )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegistrazioniCall;
